/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListRiskResultsRequest,
  ListRiskResultsSecurity,
} from "../models/operations/listriskresults.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRiskListResultsQuery,
  prefetchRiskListResults,
  queryKeyRiskListResults,
  RiskListResultsQueryData,
} from "./riskListResults.core.js";
export {
  buildRiskListResultsQuery,
  prefetchRiskListResults,
  queryKeyRiskListResults,
  type RiskListResultsQueryData,
};

export type RiskListResultsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listRiskResults risk
 *
 * @remarks
 * List risk analysis results for the current project.
 */
export function useRiskListResults(
  request?: ListRiskResultsRequest | undefined,
  security?: ListRiskResultsSecurity | undefined,
  options?: QueryHookOptions<
    RiskListResultsQueryData,
    RiskListResultsQueryError
  >,
): UseQueryResult<RiskListResultsQueryData, RiskListResultsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRiskListResultsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listRiskResults risk
 *
 * @remarks
 * List risk analysis results for the current project.
 */
export function useRiskListResultsSuspense(
  request?: ListRiskResultsRequest | undefined,
  security?: ListRiskResultsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RiskListResultsQueryData,
    RiskListResultsQueryError
  >,
): UseSuspenseQueryResult<RiskListResultsQueryData, RiskListResultsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRiskListResultsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRiskListResultsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      policyId?: string | undefined;
      chatId?: string | undefined;
      mcpServerId?: string | undefined;
      resultId?: string | undefined;
      executionId?: string | undefined;
      category?: string | undefined;
      ruleId?: string | undefined;
      userId?: string | undefined;
      externalUserIds?: Array<string> | undefined;
      uniqueMatch?: boolean | undefined;
      nonAssistant?: boolean | undefined;
      assistantId?: string | undefined;
      from?: Date | undefined;
      to?: Date | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RiskListResultsQueryData,
): RiskListResultsQueryData | undefined {
  const key = queryKeyRiskListResults(...queryKeyBase);

  return client.setQueryData<RiskListResultsQueryData>(key, data);
}

export function invalidateRiskListResults(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      policyId?: string | undefined;
      chatId?: string | undefined;
      mcpServerId?: string | undefined;
      resultId?: string | undefined;
      executionId?: string | undefined;
      category?: string | undefined;
      ruleId?: string | undefined;
      userId?: string | undefined;
      externalUserIds?: Array<string> | undefined;
      uniqueMatch?: boolean | undefined;
      nonAssistant?: boolean | undefined;
      assistantId?: string | undefined;
      from?: Date | undefined;
      to?: Date | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "results", "list", ...queryKeyBase],
  });
}

export function invalidateAllRiskListResults(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "results", "list"],
  });
}
